import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "fileInput", "progressContainer", "progressText", "documentName"]
  static values = {
    uploadUrl: String,
    reorderUrl: String,
    renameUrl: String
  }

  connect() {
    // Prevent default drag behaviors on the whole page
    window.addEventListener("dragover", this.preventDefaults)
    window.addEventListener("drop", this.preventDefaults)
  }

  disconnect() {
    window.removeEventListener("dragover", this.preventDefaults)
    window.removeEventListener("drop", this.preventDefaults)
  }

  preventDefaults = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  triggerFileInput() {
    this.fileInputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-blue-500", "dark:border-blue-400", "bg-blue-50", "dark:bg-blue-900/20")
  }

  dragenter(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-blue-500", "dark:border-blue-400", "bg-blue-50", "dark:bg-blue-900/20")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-blue-500", "dark:border-blue-400", "bg-blue-50", "dark:bg-blue-900/20")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-blue-500", "dark:border-blue-400", "bg-blue-50", "dark:bg-blue-900/20")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
  }

  filesSelected(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
    // Reset input so same file can be selected again
    event.target.value = ""
  }

  async uploadFiles(files) {
    this.showProgress()

    for (const file of files) {
      await this.uploadFile(file)
    }

    this.hideProgress()
  }

  async uploadFile(file) {
    // Validate file type
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"]
    if (!allowedTypes.includes(file.type)) {
      alert(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files.`)
      return
    }

    this.progressTextTarget.textContent = `Uploading ${file.name}...`

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: formData
      })

      if (response.ok) {
        // Turbo will handle the stream response automatically
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        const data = await response.json()
        alert(data.error || "Failed to upload file")
      }
    } catch (error) {
      console.error("Upload error:", error)
      alert("Failed to upload file. Please try again.")
    }
  }

  showProgress() {
    this.progressContainerTarget.classList.remove("hidden")
  }

  hideProgress() {
    this.progressContainerTarget.classList.add("hidden")
  }

  // Handle reorder after sortable ends
  async onReorder(event) {
    const items = this.element.querySelectorAll("[data-id]")
    const documentIds = Array.from(items).map(item => item.dataset.id)

    try {
      const response = await fetch(this.reorderUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ document_ids: documentIds })
      })

      if (!response.ok) {
        console.error("Failed to save order")
      }
    } catch (error) {
      console.error("Reorder error:", error)
    }
  }

  // Rename document
  startRename(event) {
    const documentId = event.currentTarget.dataset.documentId
    const currentName = event.currentTarget.dataset.documentName ||
                        event.currentTarget.textContent.trim()

    const newName = prompt("Enter new name:", currentName)
    if (newName && newName !== currentName) {
      this.renameDocument(documentId, newName)
    }
  }

  async renameDocument(documentId, newName) {
    try {
      const response = await fetch(this.renameUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ document_id: documentId, name: newName })
      })

      if (response.ok) {
        // Update the name in the DOM
        const nameElement = this.element.querySelector(`[data-document-id="${documentId}"]`)
        if (nameElement) {
          nameElement.textContent = newName
        }
      } else {
        alert("Failed to rename document")
      }
    } catch (error) {
      console.error("Rename error:", error)
      alert("Failed to rename document")
    }
  }
}
